'use client';

import {
	createContext,
	type ReactNode,
	useCallback,
	useContext,
	useMemo,
	useRef,
	useState
} from 'react';

import { api } from '$lib/api/client';
import type { SubscriptionResponse } from '$lib/api/types';

type SubscriptionContextValue = {
	subscription: SubscriptionResponse | null;
	loading: boolean;
	isPastDue: boolean;
	isExpired: boolean;
	needsAttention: boolean;
	load: () => Promise<void>;
	clear: () => void;
	setLocal: (subscription: SubscriptionResponse | null) => void;
};

const SubscriptionContext = createContext<SubscriptionContextValue | null>(null);

export function SubscriptionProvider({ children }: { children: ReactNode }) {
	const [subscription, setSubscription] = useState<SubscriptionResponse | null>(null);
	const [loading, setLoading] = useState(false);
	const versionRef = useRef(0);

	const nextVersion = useCallback(() => {
		versionRef.current += 1;
		return versionRef.current;
	}, []);

	const load = useCallback(async () => {
		const version = nextVersion();
		setLoading(true);
		try {
			const next = await api.get<SubscriptionResponse>('/billing/subscription');
			if (version === versionRef.current) setSubscription(next);
		} catch {
			if (version === versionRef.current) setSubscription(null);
		} finally {
			if (version === versionRef.current) setLoading(false);
		}
	}, [nextVersion]);

	const clear = useCallback(() => {
		nextVersion();
		setSubscription(null);
		setLoading(false);
	}, [nextVersion]);

	const setLocal = useCallback(
		(next: SubscriptionResponse | null) => {
			nextVersion();
			setSubscription(next);
		},
		[nextVersion]
	);

	const value = useMemo(
		() => ({
			subscription,
			loading,
			isPastDue: subscription?.status === 'PAST_DUE',
			isExpired: subscription?.status === 'EXPIRED',
			needsAttention: subscription?.status === 'PAST_DUE' || subscription?.status === 'EXPIRED',
			load,
			clear,
			setLocal
		}),
		[subscription, loading, load, clear, setLocal]
	);

	return <SubscriptionContext.Provider value={value}>{children}</SubscriptionContext.Provider>;
}

export function useSubscription() {
	const context = useContext(SubscriptionContext);
	if (!context) throw new Error('useSubscription must be used inside SubscriptionProvider');
	return context;
}
